import { motion } from 'framer-motion';
import { Check, ChevronDown, Minus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { BottomSheet, Button, useToast } from '../components';
import { useActions, useAppData } from '../data';
import { isProActive } from '../data/selectors';
import { cn } from '../lib/cn';
import { PRO_FEATURES, type ProFeatureId } from '../lib/proFeatures';
import { springQuick, tapScale } from '../tokens';

type Plan = 'monthly' | 'yearly';

const PRO_GRID: ProFeatureId[] = [
  'forecast',
  'netWorth',
  'unlimitedRules',
  'unlimitedGoals',
  'advancedInsights',
  'pdfReports',
  'premiumThemes',
  'sharedBudgets',
  'aiAssistant',
];

const COMPARE: Array<{ label: string; free: boolean; pro: boolean }> = [
  { label: 'All 4 budget models', free: true, pro: true },
  { label: 'Manual transactions', free: true, pro: true },
  { label: 'Quick-add presets', free: true, pro: true },
  { label: 'Merchant memory', free: true, pro: true },
  { label: 'All built-in tools', free: true, pro: true },
  { label: 'Full offline support', free: true, pro: true },
  { label: 'Base themes', free: true, pro: true },
  { label: 'CSV export', free: true, pro: true },
  { label: 'Up to 5 rules / 3 goals', free: true, pro: true },
  { label: 'Cash flow forecast (30d)', free: false, pro: true },
  { label: 'Net worth tracking', free: false, pro: true },
  { label: 'Unlimited rules & goals', free: false, pro: true },
  { label: 'Advanced insights', free: false, pro: true },
  { label: 'PDF reports', free: false, pro: true },
  { label: 'Premium themes', free: false, pro: true },
  { label: 'Shared budgets (soon)', free: false, pro: true },
  { label: 'AI assistant (soon)', free: false, pro: true },
  { label: 'Priority support', free: false, pro: true },
];

type Props = { open: boolean; onClose: () => void };

/**
 * Pro paywall. Lead with the value prop ($4.99/$49.99, 7-day trial,
 * no third parties touching your accounts), feature grid of what Pro
 * unlocks, expandable Free-vs-Pro compare table, and a "why we charge"
 * footer to set expectations on the business model.
 */
export function PaywallSheet({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();

  const [plan, setPlan] = useState<Plan>('yearly');
  const [compareOpen, setCompareOpen] = useState(false);

  const proActive = isProActive(data);
  const inTrial = !!data.proTrial && !data.proPurchased;
  const trialStartedMs = data.proTrial
    ? Date.parse(data.proTrial.started)
    : Number.NaN;
  const trialDaysLeft = Number.isFinite(trialStartedMs)
    ? Math.max(
        0,
        Math.ceil(
          (trialStartedMs + 7 * 24 * 60 * 60 * 1000 - Date.now()) /
            (24 * 60 * 60 * 1000),
        ),
      )
    : 0;

  const startTrial = () => {
    if (data.proTrial) {
      toast.show({
        message: `Trial already running. ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`,
        kind: 'info',
      });
      onClose();
      return;
    }
    actions.startProTrial();
    toast.show({
      message: '7-day Pro trial started. Enjoy!',
      kind: 'success',
      duration: 4000,
    });
    onClose();
  };

  const restore = () => {
    if (data.proPurchased) {
      toast.show({ message: 'You’re already on Pro', kind: 'info' });
    } else {
      toast.show({
        message: 'Nothing to restore on this device',
        kind: 'info',
      });
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="px-card-pad pt-2 pb-4 flex flex-col items-center text-center gap-3">
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 250, damping: 22 }}
          className="w-16 h-16 rounded-pill bg-warning/15 inline-flex items-center justify-center text-warning shadow-md"
        >
          <Sparkles size={28} strokeWidth={1.5} />
        </motion.div>
        <div>
          <h2 className="text-[22px] font-semibold tracking-tight text-text-primary">
            Margin Pro
          </h2>
          <p className="mt-1 text-[14px] font-medium text-text-secondary">
            $4.99/mo or $49.99/yr · 7-day free trial
          </p>
          <p className="mt-2 text-[13px] text-text-secondary leading-relaxed max-w-[340px]">
            No third-party syncs, no aggregators touching your accounts. Just
            powerful tools for people who track their own money.
          </p>
        </div>

        {/* Plan toggle */}
        <div className="w-full">
          <div className="grid grid-cols-2 gap-2">
            <PlanCard
              active={plan === 'monthly'}
              title="Monthly"
              price="$4.99"
              cadence="/month"
              onClick={() => setPlan('monthly')}
            />
            <PlanCard
              active={plan === 'yearly'}
              title="Yearly"
              price="$49.99"
              cadence="/year"
              badge="Save 17%"
              onClick={() => setPlan('yearly')}
            />
          </div>
        </div>

        {/* Pro feature grid (2-col) */}
        <div className="w-full grid grid-cols-2 gap-2 mt-1">
          {PRO_GRID.map((id) => {
            const f = PRO_FEATURES[id];
            const Icon = f.icon;
            return (
              <div
                key={id}
                className="rounded-card bg-surface-secondary p-3 text-left flex flex-col gap-1 min-h-[88px]"
              >
                <div className="flex items-center gap-2">
                  <Icon size={14} strokeWidth={1.75} className="text-accent" />
                  <span className="text-[12px] font-semibold text-text-primary truncate">
                    {f.title}
                  </span>
                </div>
                <p className="text-[11px] text-text-secondary leading-snug">
                  {f.oneLiner}
                </p>
                {f.comingSoon ? (
                  <span className="text-[10px] uppercase tracking-[0.05em] text-text-tertiary">
                    Coming soon
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Compare expandable */}
        <button
          type="button"
          onClick={() => setCompareOpen((v) => !v)}
          aria-expanded={compareOpen}
          className="w-full mt-1 inline-flex items-center justify-center gap-1.5 text-[13px] font-medium text-accent py-2"
        >
          Compare plans
          <motion.span
            animate={{ rotate: compareOpen ? 180 : 0 }}
            transition={springQuick}
            className="inline-flex"
          >
            <ChevronDown size={14} strokeWidth={2} />
          </motion.span>
        </button>
        {compareOpen ? (
          <div className="w-full rounded-card bg-surface-secondary overflow-hidden">
            <div className="grid grid-cols-[1fr_64px_64px] gap-x-2 px-3 py-2 text-[11px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
              <span className="text-left">Feature</span>
              <span className="text-center">Free</span>
              <span className="text-center text-accent">Pro</span>
            </div>
            {COMPARE.map((row, i) => (
              <div
                key={row.label}
                className={cn(
                  'grid grid-cols-[1fr_64px_64px] gap-x-2 px-3 py-2 items-center',
                  i % 2 === 1 ? 'bg-surface' : '',
                )}
              >
                <span className="text-[13px] text-text-primary text-left truncate">
                  {row.label}
                </span>
                <span className="flex items-center justify-center text-text-tertiary">
                  {row.free ? (
                    <Check
                      size={14}
                      strokeWidth={2.4}
                      className="text-positive"
                    />
                  ) : (
                    <Minus size={14} strokeWidth={2} />
                  )}
                </span>
                <span className="flex items-center justify-center">
                  {row.pro ? (
                    <Check
                      size={14}
                      strokeWidth={2.4}
                      className="text-accent"
                    />
                  ) : (
                    <Minus
                      size={14}
                      strokeWidth={2}
                      className="text-text-tertiary"
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="w-full flex flex-col gap-2 mt-2">
          {proActive ? (
            <div className="rounded-button bg-positive/10 text-positive py-3 text-[14px] font-medium">
              {data.proPurchased
                ? 'Pro is active. Thanks for supporting Margin.'
                : `Trial active · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`}
            </div>
          ) : (
            <Button
              variant="primary"
              size="lg"
              fullWidth
              className="font-semibold"
              onClick={startTrial}
            >
              Start 7-day free trial
            </Button>
          )}
          <button
            type="button"
            onClick={restore}
            className="text-[13px] font-medium text-accent py-3 min-h-tap"
          >
            Already subscribed? Restore
          </button>
          {!proActive && !inTrial ? (
            <p className="text-[11px] text-text-tertiary leading-relaxed max-w-[340px] mx-auto">
              Margin makes money from subscriptions, not your data. No ads,
              no data sales, no third-party middlemen. Cancel anytime.
            </p>
          ) : null}
        </div>
      </div>
    </BottomSheet>
  );
}

function PlanCard({
  active,
  title,
  price,
  cadence,
  badge,
  onClick,
}: {
  active: boolean;
  title: string;
  price: string;
  cadence: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-pressed={active}
      className={cn(
        'rounded-card p-3 text-left relative',
        active ? 'bg-accent/10' : 'bg-surface-secondary',
      )}
    >
      {badge ? (
        <span className="absolute top-2 right-2 inline-flex items-center justify-center rounded-pill px-2 py-[2px] bg-positive/15 text-positive text-[10px] font-semibold uppercase tracking-[0.04em] leading-none">
          {badge}
        </span>
      ) : null}
      <div
        className={cn(
          'text-[12px] font-medium uppercase tracking-[0.05em]',
          active ? 'text-accent' : 'text-text-tertiary',
        )}
      >
        {title}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span
          className={cn(
            'text-[20px] font-semibold font-mono tabular-nums',
            active ? 'text-text-primary' : 'text-text-secondary',
          )}
        >
          {price}
        </span>
        <span className="text-[12px] text-text-tertiary">{cadence}</span>
      </div>
    </motion.button>
  );
}
